import { useEffect } from 'react'
import { IconChevronsLeft } from '@tabler/icons-react'
import { Layout } from '../custom/layout'
import { Button } from '../custom/button'
import Nav from './nav'
import { cn } from '@/lib/utils'
import { useFilteredSidelinks } from '@/hooks/use-filtered-sidelinks'
import { sidelinks } from '@/routes'
import { Loader } from '../custom/loader'
import { UserNav } from './user-nav'

interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  isCollapsed: boolean
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>
  isLoading?: boolean
  navOpened: boolean
  setNavOpened: React.Dispatch<React.SetStateAction<boolean>>
}

export default function Sidebar({
  className,
  isCollapsed,
  setIsCollapsed,
  isLoading,
  navOpened,
  setNavOpened,
}: SidebarProps) {
  const filteredSidelinks = useFilteredSidelinks(sidelinks)

  /* Make body not scrollable when navBar is opened */
  useEffect(() => {
    if (navOpened) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [navOpened])

  return (
    <aside
      className={cn(
        `fixed left-0 right-0 top-0 z-50 w-full border-r-2 border-r-muted bg-card transition-[width] md:bottom-0 md:right-auto md:h-svh ${isCollapsed ? 'md:w-14' : 'md:w-64'}`,
        className
      )}
    >
      {/* Overlay in mobile */}
      <div
        onClick={() => setNavOpened(false)}
        className={`transition-[opacity] delay-100 duration-700 ${navOpened ? 'opacity-50' : 'pointer-events-none opacity-0'} fixed inset-x-0 bottom-0 top-28 md:hidden`}
      />

      <Layout fixed>
        {/* Header */}
        <Layout.Header
          sticky
          className='z-50 flex flex-nowrap justify-between bg-transparent px-4 py-3 shadow-sm  md:px-4'
        >
          <div className={`flex items-center ${!isCollapsed ? 'gap-2' : ''}`}>
            <svg
              width='27'
              height='40'
              viewBox='0 0 27 40'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M22.0108 36.7223L23.4002 35.3442V4.84294L22.0037 3.45808H4.66612L3.27713 4.83602V35.3442L4.66612 36.7223H22.0108V36.7223ZM10.8234 35.0589H15.8539V33.3957H10.8234V35.0589ZM3.27713 40.0486L-0.0769101 36.8118C-0.0769101 25.6396 -0.0785451 14.6304 -0.0769101 3.45808L3.27713 0.131592H23.3857L26.7539 3.45808C26.7518 14.6304 26.7539 25.5498 26.7539 36.7223L23.4002 40.0486H3.27713V40.0486Z'
                fill='#19B423'
              />
            </svg>

            <div
              className={`flex flex-col justify-end truncate text-4xl font-bold tracking-wide  ${isCollapsed ? 'invisible w-0' : 'visible w-auto'}`}
            >
              aros
            </div>
          </div>

          {/* Profile in mobile */}
          <div className='md:hidden'>
            <UserNav />
          </div>
        </Layout.Header>

        {/* Navigation links */}
        {isLoading ? (
          <div className='flex h-full w-full items-center justify-center'>
            <Loader />
          </div>
        ) : (
          filteredSidelinks.length > 0 && (
            <Nav
              id='sidebar-menu'
              className={`z-40 flex-1 overflow-auto ${navOpened ? 'fixed inset-x-0 bottom-0 top-28 bg-card md:static md:inset-auto md:h-full md:max-h-screen' : 'max-h-0 py-0 md:h-full md:max-h-screen md:py-2'}`}
              closeNav={() => setNavOpened(false)}
              isCollapsed={isCollapsed}
              links={filteredSidelinks}
            />
          )
        )}

        <Button
          onClick={() => setIsCollapsed((prev) => !prev)}
          size='icon'
          variant='outline'
          className='absolute -right-5 top-1/2 z-50 hidden rounded-full md:inline-flex'
        >
          <IconChevronsLeft
            stroke={1.5}
            className={`h-5 w-5 ${isCollapsed ? 'rotate-180' : ''}`}
          />
        </Button>
      </Layout>
    </aside>
  )
}
